import { useEffect, useRef, useState } from 'preact/hooks';
import { t } from '../i18n/index.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { GameIcon } from '../components/GameIcon.js';
import { ChatPanel } from '../components/ChatPanel.js';
import { RulesDialog } from '../components/RulesDialog.js';
import { gameOutcome, gameResultReason } from '../components/GameResultDialog.js';
import { useServerNow } from '../arena/useArena.js';
import { GameOverContext } from '../games/common/TurnStatus.js';
import { BattleshipGame } from '../games/battleship/BattleshipGame.js';
import { OthelloGame } from '../games/othello/OthelloGame.js';
import { MemoryGame } from '../games/memory/MemoryGame.js';
import { ChessGame } from '../games/chess/ChessGame.js';
import { SnakeGame, useSnakeState } from '../games/snake/SnakeGame.js';
import { TetraxGame, useTetraxState } from '../games/tetrax/TetraxGame.js';
import type { Realtime } from '../realtime.js';
import type { ActiveGameView, ArenaResult, GameEndedView, GameStateView, Role } from '../../shared/protocol.js';
import type { BattleshipView } from '../../shared/games/battleship/index.js';
import type { OthelloView } from '../../shared/games/othello/index.js';
import type { MemoryView } from '../../shared/games/memory/index.js';
import type { ChessView } from '../../shared/games/chess/index.js';

/**
 * Keeps the full game state in sync: pushed by the server, re-requested after reconnecting.
 * After the game has ended the last state is kept (the server no longer has one).
 */
function useGameState(realtime: Realtime, gameId: number, ended: boolean): GameStateView | null {
  const [state, setState] = useState<GameStateView | null>(null);
  const endedRef = useRef(ended);
  endedRef.current = ended;
  const socket = realtime.socket;
  useEffect(() => {
    if (!socket) return;
    const onState = (s: GameStateView) => setState(s);
    const sync = () =>
      socket.emit('game:sync', (r: ArenaResult<{ state: GameStateView | null }>) => {
        if (r.ok && (r.state || !endedRef.current)) setState(r.state);
      });
    socket.on('game:state', onState);
    socket.on('connect', sync);
    if (socket.connected) sync();
    return () => {
      socket.off('game:state', onState);
      socket.off('connect', sync);
    };
  }, [socket, gameId]);
  return state && state.id === gameId ? state : null;
}

/**
 * Active game screen: common frame (opponent, resign) + the game module's own UI. When the game
 * has ended (`ended`), the final position stays on screen behind the result (user request
 * 2026-09-29) until the player returns to the arena.
 */
export function GameView(props: {
  game: ActiveGameView;
  realtime: Realtime;
  role: Role;
  ended?: { result: GameEndedView; userId: number; maintenance: boolean; onLeave: () => void };
}) {
  const { game, realtime, ended } = props;
  const isSnake = game.gameType === 'snake';
  const state = useGameState(realtime, game.id, Boolean(ended));
  // Snake (decision 2026-09-30): a real-time game with its own state.
  const snake = useSnakeState(realtime.socket, game.id, isSnake);
  // Tetrax (2026-10-03): real-time as well, each browser runs its own board.
  const isTetrax = game.gameType === 'tetrax';
  const tetrax = useTetraxState(realtime.socket, game.id, isTetrax);
  const realtimeState = isSnake ? snake.state : isTetrax ? tetrax.state : null;
  const realtimeOpponent = realtimeState?.players[1 - realtimeState.mySeat];
  const opponentOnline = realtimeState ? realtimeOpponent?.online : state?.opponent.online;
  const opponentIsAi = isSnake || isTetrax ? realtimeOpponent?.isAi : state?.opponent.isAi;
  const myId = isSnake || isTetrax ? realtimeState?.players[realtimeState.mySeat]?.id : state?.me.id;
  const serverNow = useServerNow(realtime.clockOffset, 200);
  const [confirming, setConfirming] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resign = () => {
    setConfirming(false);
    realtime.socket?.emit('game:resign', (r: ArenaResult) => {
      if (!r.ok) setError(t.arena.errors[r.error]);
    });
  };

  const outcome = ended ? gameOutcome(ended.result, ended.userId) : null;
  const over =
    ended && outcome
      ? { outcome, text: t.result.ended(t.result.title[outcome], gameResultReason(ended.result, ended.userId, ended.maintenance)) }
      : null;
  // Without a turn nothing can be clicked and no timers run.
  const shown = state && over ? { ...state, turn: null } : state;

  return (
    <section class="game-view">
      <div class="game-head">
        <h2>
          <GameIcon game={game.gameType} size={30} /> {t.games[game.gameType].name}
        </h2>
        <div class="versus">
          {t.game.opponent}:{' '}
          {!over && (
            <>
              <span class={`status-dot ${opponentOnline === false ? 'status-idle' : 'status-available'}`} />{' '}
            </>
          )}
          <strong>{game.opponent.displayName}</strong>
        </div>
        {!over && realtime.socket && myId !== undefined && opponentIsAi === false && (
          <ChatPanel socket={realtime.socket} gameId={game.id} myId={myId} role={props.role} />
        )}
        <button class="btn btn-small" onClick={() => setShowRules(true)}>
          📖 {t.rules.buttonShort}
        </button>
        {ended ? (
          <button class="btn btn-primary btn-small" onClick={ended.onLeave}>
            {t.result.backToArena}
          </button>
        ) : (
          <button class="btn btn-danger btn-small" onClick={() => setConfirming(true)}>
            {t.game.resign}
          </button>
        )}
      </div>
      {error && <p class="error">{error}</p>}

      <GameOverContext.Provider value={over}>
        {isSnake ? (
          snake.state && realtime.socket ? (
            <SnakeGame state={snake.state} offset={snake.offset} socket={realtime.socket} />
          ) : (
            <div class="card muted">{over ? over.text : t.status.connecting}</div>
          )
        ) : isTetrax ? (
          tetrax.state && realtime.socket ? (
            <TetraxGame state={tetrax.state} offset={tetrax.offset} socket={realtime.socket} />
          ) : (
            <div class="card muted">{over ? over.text : t.status.connecting}</div>
          )
        ) : !shown || !realtime.socket ? (
          <div class="card muted">{over ? over.text : t.status.connecting}</div>
        ) : over && shown.status === 'placement' ? (
          // Ended before the fleets were placed: there is no position to show.
          <div class="card muted">{over.text}</div>
        ) : shown.gameType === 'battleship' ? (
          <BattleshipGame state={shown as GameStateView<BattleshipView>} socket={realtime.socket} serverNow={serverNow} />
        ) : shown.gameType === 'chess' ? (
          <ChessGame state={shown as GameStateView<ChessView>} socket={realtime.socket} serverNow={serverNow} />
        ) : shown.gameType === 'othello' ? (
          <OthelloGame state={shown as GameStateView<OthelloView>} socket={realtime.socket} serverNow={serverNow} />
        ) : (
          <MemoryGame state={shown as GameStateView<MemoryView>} socket={realtime.socket} serverNow={serverNow} />
        )}
      </GameOverContext.Provider>

      {showRules && <RulesDialog game={game.gameType} settings={state?.settings} onClose={() => setShowRules(false)} />}
      {confirming && !ended && (
        <ConfirmDialog
          title={t.game.resignTitle}
          confirmLabel={t.game.resign}
          cancelLabel={t.game.cancel}
          danger
          onConfirm={resign}
          onCancel={() => setConfirming(false)}
        >
          <p>{t.game.resignText}</p>
        </ConfirmDialog>
      )}
    </section>
  );
}
